
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from "@/utils/toast";
import { Loader2, Mail, Lock, User } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AuthFormProps {
  mode: 'login' | 'signup';
}

const AuthForm: React.FC<AuthFormProps> = ({ mode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, signup, resetPassword } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Auth mode states
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  
  const handleEmailPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      if (forgotPasswordMode) {
        await resetPassword(email);
        setResetSent(true);
        toast.success("Password reset email sent! Check your inbox.");
      } else if (mode === 'login') {
        await login(email, password);
        navigate('/chat');
      } else {
        await signup(email, password, displayName);
        navigate('/chat');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Authentication failed';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setForgotPasswordMode(false);
    setResetSent(false);
    setError('');
  };

  // Determine if we're on signup or login
  const isSignUp = mode === 'signup' || location.pathname === '/signup';
  
  // Password Reset Mode
  if (forgotPasswordMode) {
    return (
      <div className="w-full max-w-md mx-auto rounded-2xl animate-fade-in">
        <h2 className="text-2xl font-semibold mb-6 text-center text-[#0066CC]">
          {resetSent ? "Check Your Email" : "Reset Password"}
        </h2>
        
        {error && (
          <div className="mb-6 p-3 bg-red-500/10 border border-red-500/20 rounded-md text-sm text-red-500 animate-fade-in">
            {error}
          </div>
        )}
        
        {resetSent ? (
          <div className="space-y-5">
            <p className="text-sm text-foreground/80">
              We've sent password reset instructions to your email address. Please check your inbox and follow the link to reset your password.
            </p>
            
            <Button 
              type="button" 
              className="w-full bg-[#0066CC] hover:bg-[#0055AA] text-white"
              onClick={handleBackToLogin}
            >
              Back to Login
            </Button>
          </div>
        ) : (
          <form onSubmit={handleEmailPasswordSubmit} className="space-y-5">
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium flex items-center">
                <Mail size={16} className="mr-2 text-[#0066CC]" />
                <span>Email Address</span>
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border-[#D3E4FD] focus-visible:ring-[#0066CC]"
                placeholder="you@example.com"
                required
                disabled={isLoading}
                autoComplete="email"
              />
            </div>
            
            <Button 
              type="submit" 
              className={cn(
                "w-full bg-[#0066CC] hover:bg-[#0055AA] text-white", 
                isLoading && "opacity-80"
              )}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending reset link...
                </span>
              ) : (
                "Send Reset Link"
              )}
            </Button>
            
            <div className="text-center mt-6">
              <Button 
                type="button" 
                variant="link" 
                onClick={handleBackToLogin}
                className="text-[#0066CC]"
              >
                Back to Login
              </Button>
            </div>
          </form>
        )}
      </div>
    );
  }
  
  // Regular Auth Form (Email/Password)
  return (
    <div className="w-full max-w-md mx-auto rounded-2xl">
      {/* Removed duplicate heading since it's already in the parent component */}
      
      {error && (
        <div className="mb-6 p-3 bg-red-500/10 border border-red-500/20 rounded-md text-sm text-red-500 animate-fade-in">
          {error}
        </div>
      )}
      
      <form onSubmit={handleEmailPasswordSubmit} className="space-y-5">
        {isSignUp && (
          <div className="space-y-2">
            <label htmlFor="displayName" className="text-sm font-medium flex items-center">
              <User size={16} className="mr-2 text-[#0066CC]" />
              <span>Full Name</span>
            </label>
            <Input
              id="displayName"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full border-[#D3E4FD] focus-visible:ring-[#0066CC]"
              placeholder="Your full name"
              disabled={isLoading}
              autoComplete="name"
            />
          </div>
        )}
        
        <div className="space-y-2">
          <label htmlFor="email" className="text-sm font-medium flex items-center">
            <Mail size={16} className="mr-2 text-[#0066CC]" />
            <span>Email Address</span>
          </label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full border-[#D3E4FD] focus-visible:ring-[#0066CC]"
            placeholder="you@example.com"
            required
            disabled={isLoading}
            autoComplete="email"
          />
        </div>
        
        <div className="space-y-2">
          <label htmlFor="password" className="text-sm font-medium flex items-center">
            <Lock size={16} className="mr-2 text-[#0066CC]" />
            <span>Password</span>
          </label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full border-[#D3E4FD] focus-visible:ring-[#0066CC]"
            placeholder="••••••••"
            required
            disabled={isLoading}
            autoComplete={isSignUp ? "new-password" : "current-password"}
          />
        </div>
        
        {!isSignUp && (
          <div className="flex justify-end">
            <Button 
              variant="link" 
              type="button" 
              onClick={() => setForgotPasswordMode(true)}
              className="text-sm text-[#0066CC] hover:underline p-0 h-auto"
            >
              Forgot password?
            </Button>
          </div>
        )}
        
        <Button 
          type="submit" 
          className={cn(
            "w-full bg-[#0066CC] hover:bg-[#0055AA] text-white h-12 mt-4", 
            isLoading && "opacity-80"
          )}
          disabled={isLoading}
        >
          {isLoading ? (
            <span className="flex items-center justify-center">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {isSignUp ? 'Creating account...' : 'Logging in...'}
            </span>
          ) : (
            isSignUp ? 'Sign up' : 'Log in'
          )}
        </Button>
        
        <div className="text-center mt-6">
          <p className="text-sm text-foreground/70">
            {isSignUp ? (
              <>
                Already have an account?{' '}
                <a href="/login" className="text-[#0066CC] hover:underline">
                  Log in
                </a>
              </>
            ) : (
              <>
                Don't have an account?{' '}
                <a href="/signup" className="text-[#0066CC] hover:underline">
                  Sign up
                </a>
              </>
            )}
          </p>
        </div>
      </form>
    </div>
  );
};

export default AuthForm;
